import axios from "axios";
import Cookies from "js-cookie";
import ms, { StringValue } from "ms";
import Router from "next/router";

import { logout } from "@/redux/reducers/user";
import { store } from "@/redux/store";
import logger from "@/runtime-utils/observability/logger";
import { clearSessionDeadline, saveSessionDeadline } from "@/session/store";
import {
  getPublicEnv,
  getPublicNodeEnv,
} from "@/runtime-utils/config/public-env";

export const BACKEND = getPublicEnv("NEXT_PUBLIC_BASE_URL");
export const API_URL = BACKEND?.endsWith("/")
  ? `${BACKEND}api/v1`
  : `${BACKEND}/api/v1`;
export const TENANT_API_URL = "/tenant";
export const PUBLIC_SITE_URL = "/admin/public";
export const ADMIN_SITE_URL = "/admin/";

export const IMAGE_PROXY = "/api/image-proxy?url=";

export const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    "x-timezone-offset": (-new Date().getTimezoneOffset()).toString(),
  },
  withCredentials: true,
});

const { CancelToken } = axios;
const PUBLIC_NODE_ENV = getPublicNodeEnv();

const clearSessionCookies = () => {
  const removalVariants: Array<Parameters<typeof Cookies.remove>[1]> = [
    { path: "/" },
    ...(PUBLIC_NODE_ENV === "production"
      ? [
          { path: "/", secure: true },
          { path: "/", domain: ".wisterias.co.uk", secure: true },
        ]
      : []),
    ...(PUBLIC_NODE_ENV === "test"
      ? [
          { path: "/", secure: true },
          { path: "/", domain: ".wisterias.co.uk", secure: true },
          { path: "/", domain: ".test.wisterias.co.uk", secure: true },
        ]
      : []),
  ];

  for (const options of removalVariants) {
    Cookies.remove("token", options);
    Cookies.remove("tenant", options);
  }

  Cookies.remove("token");
  Cookies.remove("tenant");
};

export const createCancelToken = () => {
  const source = CancelToken.source();
  return {
    token: source.token,
    cancel: source.cancel,
  };
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }
    const metadata = (config as any).metadata as
      { requestId?: string; operationId?: string } | undefined;
    const requestId =
      config.headers["x-request-id"] ||
      metadata?.requestId ||
      crypto.randomUUID();
    config.headers["x-request-id"] = requestId;
    const method = (config.method || "get").toLowerCase();
    const route = config.url || "";
    const derivedOperationId = route.includes("schedule-visit")
      ? `schedule_visit.${method}`
      : route.includes("/roster")
        ? `roster.${method}`
        : undefined;
    const operationId =
      config.headers["x-operation-id"] ||
      config.headers["x-wisterias-operation-id"] ||
      metadata?.operationId ||
      derivedOperationId;
    if (operationId) config.headers["x-operation-id"] = operationId;

    const state = store.getState();
    const token = Cookies.get("token") ?? state.user?.token;
    const tenant = Cookies.get("tenant") ?? localStorage.getItem("tenant");
    const currentUser = state.user?.user as
      | {
          _id?: string;
          tenantId?: string;
          role?: string | number;
          jobRoles?: string[];
        }
      | undefined;
    const resolvedTenant =
      tenant ||
      currentUser?.tenantId ||
      getPublicEnv("NEXT_PUBLIC_OBSERVABILITY_TENANT") ||
      "anonymous";

    config.headers["x-wisterias-app"] = "customer-web";
    config.headers["x-wisterias-observability-tenant"] = resolvedTenant;

    if (currentUser?._id) {
      config.headers["x-wisterias-user-id"] = currentUser._id;
    }

    if (token) {
      config.headers.Authorization = "Bearer " + token;
    }

    if (tenant) {
      config.headers.tenant = tenant;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  async (response) => {
    const newToken = response.data.token;
    const oldToken = Cookies.get("token");

    if (newToken && oldToken !== newToken) {
      const rawExpiry = response.data?.expiresIn;
      const hasValidExpiry =
        typeof rawExpiry === "string" && rawExpiry.trim().length > 0;
      const expiryInDays = hasValidExpiry
        ? ms(rawExpiry as StringValue) / (1000 * 60 * 60 * 24)
        : undefined;

      const cookieOptions = {
        ...(typeof expiryInDays === "number" && { expires: expiryInDays }),
        path: "/",
        sameSite: "lax" as const,
        ...((PUBLIC_NODE_ENV === "production" ||
          PUBLIC_NODE_ENV === "test") && {
          secure: true,
        }),
      };
      Cookies.set("token", newToken, cookieOptions);
    }
    if (typeof response.data?.expiresIn === "string") {
      saveSessionDeadline(response.data.expiresIn);
    }
    return response;
  },
  (error) => {
    // Intentional request cancellations (e.g. superseded roster fetches) are
    // not errors — silence them so they don't pollute the console.
    const isCanceled =
      error?.name === "CanceledError" ||
      error?.code === "ERR_CANCELED" ||
      error?.message === "canceled" ||
      error?.__CANCEL__ === true;
    if (isCanceled) return Promise.reject(error);

    const status = error?.response?.status;
    const requestUrl = error?.config?.url || "";
    const responseMessage = String(error?.response?.data?.message || "");
    const isExpectedSignInAuthFailure =
      status === 401 &&
      (requestUrl.includes("/auth/login") ||
        requestUrl.includes("/admin/login"));
    const isExpectedEmptyScheduleVisit =
      status === 404 &&
      requestUrl.includes("/service-users/schedule-visit/") &&
      error?.response?.data?.message === "Schedule visit not found";
    const isExpectedMissingPreferredTerms =
      status === 404 && requestUrl.includes("/preferred-term/");

    const normalizedMessage = responseMessage.toLowerCase();
    const isLikelyAuth403 =
      status === 403 &&
      (normalizedMessage.includes("token") ||
        normalizedMessage.includes("jwt") ||
        normalizedMessage.includes("unauthorized") ||
        normalizedMessage.includes("not authorized") ||
        normalizedMessage.includes("not authenticated"));

    const shouldForceSessionReset =
      (status === 401 || isLikelyAuth403) && !isExpectedSignInAuthFailure;

    const config = error?.config;
    const metadata = config?.metadata as
      { requestId?: string; operationId?: string } | undefined;
    const requestId =
      config?.headers?.["x-request-id"] || metadata?.requestId || "unknown";
    const operationId =
      config?.headers?.["x-operation-id"] ||
      config?.headers?.["x-wisterias-operation-id"] ||
      metadata?.operationId;
    const safeError = {
      name: error?.name || "Error",
      message: String(error?.message || "Unknown error").slice(0, 300),
      code: error?.code ?? null,
      status: status ?? null,
    };

    if (
      !isExpectedSignInAuthFailure &&
      !isExpectedEmptyScheduleVisit &&
      !isExpectedMissingPreferredTerms
    ) {
      logger.error(
        {
          event: "http.request.failed",
          requestId,
          ...(operationId ? { operationId } : {}),
          method: config?.method ?? null,
          route: requestUrl,
          error: safeError,
        },
        "Axios response error"
      );
    }
    // The structured failure above is the single error record. Never log the
    // raw Axios error because it can contain authorization and cookie data.
    if (error.response && shouldForceSessionReset) {
      // localStorage.removeItem("token");
      // if (Cookies.get("token")) {
      store.dispatch(logout());

      clearSessionCookies();
      clearSessionDeadline();
      // setUserToStorage({});
      if (
        ![
          "/sign-in",
          "/sign-up",
          "/auth/forgot-password",
          "/auth/reset-password",
          "/auth/set-password",
          "/auth/set-pin",
        ].includes(window.location.pathname)
      ) {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        void Router.push(
          `/sign-in?reason=session-expired&redirect=${encodeURIComponent(window.location.pathname)}`
        );
      }
      // }
    }
    return Promise.reject(error);
  }
);

export const $public = (url: string | undefined) => {
  if (!url) return "";
  if (url.startsWith(IMAGE_PROXY)) return url;

  // Always ensure only one slash between BACKEND and url
  let cleanUrl = url;
  if (cleanUrl[0] === "/" && BACKEND && BACKEND[BACKEND.length - 1] === "/") {
    cleanUrl = cleanUrl.substring(1);
  }
  const fullUrl = BACKEND
    ? `${BACKEND}${cleanUrl[0] === "/" ? "" : "/"}${cleanUrl}`
    : cleanUrl;

  // Route all images through proxy to handle authentication properly
  // Encode the nested URL as one query value before any Image optimizer layer.
  return `${IMAGE_PROXY}${encodeURIComponent(fullUrl)}`;
};

// New helper specifically for image components. Returns a proxied URL for raster
// images (so the server can add auth/caching) but returns the direct backend
// URL for SVGs so components can render them without Next's optimizer.
export const $image = (url: string | undefined) => {
  if (!url) return "";
  if (url.startsWith(IMAGE_PROXY)) return url;

  // Check if it's an SVG file BEFORE processing the URL
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
  const isSvg = ext === "svg";

  let cleanUrl = url;
  if (cleanUrl[0] === "/" && BACKEND && BACKEND[BACKEND.length - 1] === "/") {
    cleanUrl = cleanUrl.substring(1);
  }
  const fullUrl = BACKEND
    ? `${BACKEND}${cleanUrl.startsWith("/") ? "" : "/"}${cleanUrl}`
    : cleanUrl;

  // If it's an SVG, return the direct URL so the app can render it with a normal img tag
  // (avoids next/image optimizer issues).
  if (isSvg) return fullUrl;

  // For other image types, route via the image proxy so the server can attach
  // auth headers and handle caching.
  return `${IMAGE_PROXY}${encodeURIComponent(fullUrl)}`;
};

export const $serverUrl = (url: string | undefined) => {
  if (!url) return "";
  if (url[0] === "/" && BACKEND![BACKEND!.length - 1] === "/") {
    url = url.substring(1);
    return `${BACKEND}${url}`;
  } else if (url[0] !== "/" && BACKEND![BACKEND!.length - 1] !== "/") {
    return `${BACKEND}/${url}`;
  } else {
    return `${BACKEND}${url}`;
  }
};

export const parseResponse = (response: any) => {
  if (!response) {
    return { success: false, message: "Something went wrong", error: response };
  }
  if (response.status === 401) {
    clearSessionCookies();
    return response.data || { success: false, message: "Session expired" };
  }
  if (response.status >= 200 && response.status < 300) {
    return response.data;
  } else {
    // Check if response.data exists before accessing .success
    if (response.data && response.data.success === false) {
      return {
        success: false,
        message: response.data.message || "Something went wrong",
      };
    }
    const errorMessage =
      response.data?.message ||
      response.data?.error ||
      Object.values(response.data?.errors || {})
        .flat()
        .join("\n\n") ||
      "An unexpected server error occurred (500). Please try again in a moment.";

    return {
      success: false,
      message: errorMessage,
    };
  }
};
